import axios from "axios";
import dayjs from "dayjs";
import io from "socket.io-client";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ACTION_TYPES } from "./types";
import store from "../index";
import axiosInstance from "../../services/api";
import { redirectToAuth } from "../helpers";
import { REPORT_TYPE_IDS } from "../constants";
import {
  ARRIVAL_RATES_PER_SERVICE_COLUMN_DEFINITIONS,
  ARRIVAL_RATES_SUMMARY_COLUMN_DEFINITIONS,
  AGENT_TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
  CUSTOMERS_IN_BRANCH_COLUMN_DEFINITIONS,
  SERVICE_TIME_PER_SERVICE_COLUMN_DEFINITIONS,
  TICKET_SUMMARY_COLUMN_DEFINITIONS,
  TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
  WAITING_TIME_SUMMARY_COLUMN_DEFINITIONS,
  WAITING_TIME_PER_SERVICE_COLUMN_DEFINITIONS,
  normalizeRenderConfig,
} from "../renderConfig";

const AGENT_TICKETS_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_agent_tickets_per_service,
  REPORT_TYPE_IDS.weekly_branch_agent_tickets_per_service,
  REPORT_TYPE_IDS.daily_branch_agent_tickets_per_service,
  REPORT_TYPE_IDS.monthly_branch_tickets_per_agent,
  REPORT_TYPE_IDS.weekly_branch_tickets_per_agent,
  REPORT_TYPE_IDS.daily_branch_tickets_per_agent,
]);

const NO_CHART_AGENT_TICKETS_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_agent_tickets_per_service,
  REPORT_TYPE_IDS.weekly_branch_agent_tickets_per_service,
  REPORT_TYPE_IDS.daily_branch_agent_tickets_per_service,
]);

const TICKETS_PER_SERVICE_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_tickets_per_service,
  REPORT_TYPE_IDS.weekly_branch_tickets_per_service,
  REPORT_TYPE_IDS.daily_branch_tickets_per_service,
  REPORT_TYPE_IDS.monthly_organization_tickets_per_service,
  REPORT_TYPE_IDS.weekly_organization_tickets_per_service,
  REPORT_TYPE_IDS.daily_organization_tickets_per_service,
]);

const SERVICE_TIME_PER_SERVICE_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_service_time_per_service,
  REPORT_TYPE_IDS.weekly_branch_service_time_per_service,
  REPORT_TYPE_IDS.daily_branch_service_time_per_service,
  REPORT_TYPE_IDS.monthly_organization_service_time_per_service,
  REPORT_TYPE_IDS.weekly_organization_service_time_per_service,
  REPORT_TYPE_IDS.daily_organization_service_time_per_service,
]);

const WAITING_TIME_PER_SERVICE_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_waiting_time_per_service,
  REPORT_TYPE_IDS.weekly_branch_waiting_time_per_service,
  REPORT_TYPE_IDS.daily_branch_waiting_time_per_service,
  REPORT_TYPE_IDS.monthly_organization_waiting_time_per_service,
  REPORT_TYPE_IDS.weekly_organization_waiting_time_per_service,
  REPORT_TYPE_IDS.daily_organization_waiting_time_per_service,
]);

const ARRIVAL_RATES_PER_SERVICE_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_arrival_rates_per_service,
  REPORT_TYPE_IDS.weekly_branch_arrival_rates_per_service,
  REPORT_TYPE_IDS.daily_branch_arrival_rates_per_service,
  REPORT_TYPE_IDS.monthly_organization_arrival_rates_per_service,
  REPORT_TYPE_IDS.weekly_organization_arrival_rates_per_service,
  REPORT_TYPE_IDS.daily_organization_arrival_rates_per_service,
]);

const ARRIVAL_RATES_SUMMARY_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_arrival_rates_per_day,
  REPORT_TYPE_IDS.weekly_branch_arrival_rates_per_day,
  REPORT_TYPE_IDS.monthly_organization_arrival_rates_per_day,
  REPORT_TYPE_IDS.weekly_organization_arrival_rates_per_day,
  REPORT_TYPE_IDS.monthly_branch_arrival_rates_per_hour,
  REPORT_TYPE_IDS.weekly_branch_arrival_rates_per_hour,
  REPORT_TYPE_IDS.daily_branch_arrival_rates_per_hour,
  REPORT_TYPE_IDS.monthly_organization_arrival_rates_per_hour,
  REPORT_TYPE_IDS.weekly_organization_arrival_rates_per_hour,
  REPORT_TYPE_IDS.daily_organization_arrival_rates_per_hour,
]);

const TICKET_SUMMARY_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_organization_tickets,
  REPORT_TYPE_IDS.weekly_organization_tickets,
  REPORT_TYPE_IDS.daily_organization_tickets,
  REPORT_TYPE_IDS.monthly_organization_tickets_per_branch,
  REPORT_TYPE_IDS.weekly_organization_tickets_per_branch,
  REPORT_TYPE_IDS.daily_organization_tickets_per_branch,
]);

const WAITING_TIME_SUMMARY_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_organization_waiting_time,
  REPORT_TYPE_IDS.weekly_organization_waiting_time,
  REPORT_TYPE_IDS.daily_organization_waiting_time,
]);

const CUSTOMERS_IN_BRANCH_REPORT_TYPES = new Set([
  REPORT_TYPE_IDS.monthly_branch_customers_in_branch_per_hour,
  REPORT_TYPE_IDS.weekly_branch_customers_in_branch_per_hour,
  REPORT_TYPE_IDS.daily_branch_customers_in_branch_per_hour,
]);

const normalizeReportRenderConfig = ({ reportTypeId, renderConfig }) => {
  if (AGENT_TICKETS_REPORT_TYPES.has(reportTypeId)) {
    const normalized = normalizeRenderConfig(renderConfig, {
      columnDefinitions: AGENT_TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
    });
    return NO_CHART_AGENT_TICKETS_REPORT_TYPES.has(reportTypeId)
      ? { ...normalized, charts: { enabled: false } }
      : normalized;
  }
  if (TICKETS_PER_SERVICE_REPORT_TYPES.has(reportTypeId)) {
    return normalizeRenderConfig(renderConfig, {
      columnDefinitions: TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
    });
  }
  if (SERVICE_TIME_PER_SERVICE_REPORT_TYPES.has(reportTypeId)) {
    return normalizeRenderConfig(renderConfig, {
      columnDefinitions: SERVICE_TIME_PER_SERVICE_COLUMN_DEFINITIONS,
    });
  }
  if (WAITING_TIME_PER_SERVICE_REPORT_TYPES.has(reportTypeId)) {
    return normalizeRenderConfig(renderConfig, {
      columnDefinitions: WAITING_TIME_PER_SERVICE_COLUMN_DEFINITIONS,
    });
  }
  if (ARRIVAL_RATES_PER_SERVICE_REPORT_TYPES.has(reportTypeId)) {
    return normalizeRenderConfig(renderConfig, {
      columnDefinitions: ARRIVAL_RATES_PER_SERVICE_COLUMN_DEFINITIONS,
    });
  }
  if (ARRIVAL_RATES_SUMMARY_REPORT_TYPES.has(reportTypeId)) {
    return normalizeRenderConfig(renderConfig, {
      columnDefinitions: ARRIVAL_RATES_SUMMARY_COLUMN_DEFINITIONS,
    });
  }
  if (TICKET_SUMMARY_REPORT_TYPES.has(reportTypeId)) {
    return normalizeRenderConfig(renderConfig, {
      columnDefinitions: TICKET_SUMMARY_COLUMN_DEFINITIONS,
    });
  }
  if (WAITING_TIME_SUMMARY_REPORT_TYPES.has(reportTypeId)) {
    return normalizeRenderConfig(renderConfig, {
      columnDefinitions: WAITING_TIME_SUMMARY_COLUMN_DEFINITIONS,
    });
  }
  if (CUSTOMERS_IN_BRANCH_REPORT_TYPES.has(reportTypeId)) {
    return normalizeRenderConfig(renderConfig, {
      columnDefinitions: CUSTOMERS_IN_BRANCH_COLUMN_DEFINITIONS,
    });
  }
  return normalizeRenderConfig(renderConfig);
};

axios.defaults.baseURL = "https://cx.api.averly.com.na";

let socket = {}; // Declare a variable to hold the Socket.IO client instance
export const listenToEvent =
  (eventName, updateType, organizationCheck = 0) =>
  (dispatch) => {
    socket[eventName] = io("https://cx.api.averly.com.na");
    const startType = updateType + "_START";
    const payload = 1;
    dispatch({ type: startType, payload: 1 });
    socket[eventName].on(`${eventName}-updated`, (res) => {
      let type, payload;
      if (res.new_val != null) {
        type = updateType + (res.old_val != null ? "_UPDATE" : "_CREATE");
        payload = res.new_val;
      } else {
        type = updateType + "_DELETE";
        payload = res.old_val;
      }

      // dispatch only if the payload is for the active organization
      let organizationId;
      if (organizationCheck) {
        const { userData: authenticatedUser } = store.getState().authReducer;
        organizationId =
          authenticatedUser.activeOrganization &&
          authenticatedUser.activeOrganization.length
            ? authenticatedUser.activeOrganization
            : authenticatedUser.organizationId;
      }

      if (!organizationCheck || payload.organizationId === organizationId) {
        dispatch({
          type,
          payload,
        });
      }
    });
  };

export const fetchData = async (
  endpoint,
  params,
  singleResponse,
  dispatch,
  actionType
) => {
  try {
    const res = await axiosInstance.get(endpoint, { params });
    const payload = singleResponse
      ? res.data.length
        ? res.data[0]
        : {}
      : res.data;
    dispatch({ type: actionType, payload });
  } catch (err) {
    console.error(`Error fetching data from ${endpoint}:`, err);
    redirectToAuth(err);
    throw err;
  }
};

// get reportsData
export const getReportsData =
  (organizationId, dateRange = null, reportRange = null) =>
  async (dispatch) => {
    dispatch({ type: "SET_LOADING", payload: true });
    const baseUrl = "https://worker.api.averly.com.na";
    const endpoint = `${baseUrl}/api/v1/reports/search`;
    const startDate = dayjs.unix(dateRange[0]).format("DD-MM-YYYY");
    const payload = {
      organizationId,
      startDate,
      reportRange,
    };
    const actionType = ACTION_TYPES.GET_REPORTS_DATA;
    const singleResponse = 0;
    toast.info("Fetching data");
    await fetchData(endpoint, payload, singleResponse, dispatch, actionType);
    dispatch({ type: "SET_LOADING", payload: false });
  };

// GET_ORGANIZATIONS
export const getOrganizations = (organizationId) => async (dispatch) => {
  const endpoint = "/admin/organizations/";
  const payload = {
    organizationId,
  };
  const actionType = "GET_ORGANIZATIONS";
  const singleResponse = 0;
  await fetchData(endpoint, payload, singleResponse, dispatch, actionType);
};

// GET_BRANCHES
export const getBranches = (organizationId) => async (dispatch) => {
  const endpoint = "/admin/branches/";
  const payload = {
    organizationId,
  };
  const actionType = "GET_BRANCHES";
  const singleResponse = 0;
  await fetchData(endpoint, payload, singleResponse, dispatch, actionType);
};

// GET_SERVICES
export const getServices = (organizationId, branchId) => async (dispatch) => {
  const endpoint = "/apps/qm/services/";
  const payload = {
    organizationId,
    branchId,
  };
  const actionType = "GET_SERVICES";
  const singleResponse = 0;
  await fetchData(endpoint, payload, singleResponse, dispatch, actionType);
};
// GET_SERVICES_SUBSCRIPTIONS
export const getServicesSubscriptions =
  (organizationId, branchId) => async (dispatch) => {
    const endpoint = "/apps/qm/serviceSubscriptions/";
    const payload = {
      organizationId,
      branchId,
    };
    const actionType = "GET_SERVICES_SUBSCRIPTIONS";
    const singleResponse = 0;
    await fetchData(endpoint, payload, singleResponse, dispatch, actionType);
  };
// GET_USERS
export const getUsers =
  (organizationId, branchId, dateRange = null) =>
  async (dispatch) => {
    const endpoint = "/iam/users/";
    const payload = {
      organizationId,
      branchId,
    };
    const actionType = "GET_USERS";
    const singleResponse = 0;
    await fetchData(endpoint, payload, singleResponse, dispatch, actionType);
  };

// search tickets
export const setDateRange = (dateRange) => async (dispatch) => {
  dispatch({
    type: "SET_DATE_RANGE",
    payload: dateRange,
  });
};

export const updateReportRange = (payload) => (dispatch) => {
  dispatch({
    type: "UPDATE_REPORT_RANGE",
    payload,
  });
  // do reset
  dispatch(getBranches(payload));
  dispatch(setDateRange(""));
};

export const updateOrganizationId = (payload) => (dispatch) => {
  dispatch({
    type: "UPDATE_ORGANIZATION_ID",
    payload,
  });

  dispatch(getBranches(payload));

  // do reset
  dispatch(setDateRange(""));
  dispatch({ type: ACTION_TYPES.GET_REPORTS_DATA, payload: [] });
  dispatch({ type: "GET_BRANCHES", payload: [] });
  dispatch({ type: "UPDATE_BRANCH_ID", payload: "" });
};

export const updateBranchId =
  ({ branchId, organizationId }) =>
  (dispatch) => {
    dispatch({
      type: "UPDATE_BRANCH_ID",
      payload: branchId,
    });
  };

//Get report types
export const getReportTypes = () => async (dispatch) => {
  const baseUrl = "https://worker.api.averly.com.na";
    const endpoint = `${baseUrl}/api/v1/reports/reportTypes`;
  const actionType = ACTION_TYPES.GET_REPORT_TYPES;
  const singleResponse = 0;
  await fetchData(endpoint, {}, singleResponse, dispatch, actionType);
};

export const getReportRenderSetting = (params) => async (dispatch) => {
  const baseUrl = "https://worker.api.averly.com.na";
  const endpoint = `${baseUrl}/api/v1/reports/render-settings`;
  try {
    const res = await axiosInstance.get(endpoint, { params });
    dispatch({
      type: ACTION_TYPES.SET_REPORT_RENDER_SETTING,
      payload: res.data,
    });
    return res.data;
  } catch (err) {
    redirectToAuth(err, "Could not load report presentation settings");
    throw err;
  }
};

export const saveReportRenderSetting = (payload) => async (dispatch) => {
  const baseUrl = "https://worker.api.averly.com.na";
  const endpoint = `${baseUrl}/api/v1/reports/render-settings`;
  dispatch({ type: ACTION_TYPES.SET_REPORT_RENDER_SETTING_SAVING, payload: true });
  try {
    const res = await axiosInstance.put(endpoint, {
      ...payload,
      renderConfig: normalizeReportRenderConfig(payload),
    });
    dispatch({
      type: ACTION_TYPES.SET_REPORT_RENDER_SETTING,
      payload: res.data,
    });
    toast.success("Saved report presentation settings");
    return res.data;
  } catch (err) {
    redirectToAuth(err, "Could not save report presentation settings");
    throw err;
  } finally {
    dispatch({ type: ACTION_TYPES.SET_REPORT_RENDER_SETTING_SAVING, payload: false });
  }
};
